import React, { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

interface ScrollResetContextValue {
  suppressed: boolean;
  setSuppressed: (value: boolean) => void;
}

const ScrollResetContext = createContext<ScrollResetContextValue>({
  suppressed: false,
  setSuppressed: () => undefined,
});

/**
 * Place this above any component that may suppress scroll-to-top-on-navigate.
 * Typically mounted once near the app root.
 */
export const ScrollResetProvider = ({ children }: { children: ReactNode }) => {
  const [suppressed, setSuppressedState] = useState(false);
  const setSuppressed = useCallback((value: boolean) => setSuppressedState(value), []);
  return <ScrollResetContext.Provider value={{ suppressed, setSuppressed }}>{children}</ScrollResetContext.Provider>;
};

/**
 * Call in a component that manages its own scroll position. While `active` is
 * true, suppresses the app-level scroll-to-top-on-navigate. Defaults to true.
 */
export const useSuppressScrollReset = (active = true) => {
  const { setSuppressed } = useContext(ScrollResetContext);
  useEffect(() => {
    if (!active) return;
    setSuppressed(true);
    return () => setSuppressed(false);
  }, [active, setSuppressed]);
};

export const useScrollResetSuppressed = () => useContext(ScrollResetContext).suppressed;
